import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  UserIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';

const SupportChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [humanSupportAvailable, setHumanSupportAvailable] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);
  const [showHumanSupportBanner, setShowHumanSupportBanner] = useState(true);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages when human support is active
  useEffect(() => {
    if (!conversation?.human_support_requested) return;

    const pollInterval = setInterval(async () => {
      try {
        const messagesResponse = await apiClient.get(`/support-chat/conversation/${conversation.id}/messages`);
        const newMessages = messagesResponse.data.messages || [];

        // Only update if there are new messages
        if (newMessages.length !== messages.length) {
          setMessages(newMessages);
        }
      } catch (error) {
        console.error('Failed to poll messages:', error);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [conversation?.human_support_requested, conversation?.id, messages.length]);

  const initializeChat = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Get or create conversation
      const conversationResponse = await apiClient.get('/support-chat/conversation');
      const conv = conversationResponse.data.conversation;
      setConversation(conv);

      // Load message history
      const messagesResponse = await apiClient.get(`/support-chat/conversation/${conv.id}/messages`);
      setMessages(messagesResponse.data.messages || []);

      // If no messages, send welcome message
      if (messagesResponse.data.messages.length === 0) {
        await apiClient.post(`/support-chat/conversation/${conv.id}/messages`, {
          message: 'Hello! I need help with the invoice system.'
        });
        const refreshedMessages = await apiClient.get(`/support-chat/conversation/${conv.id}/messages`);
        setMessages(refreshedMessages.data.messages || []);
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      toast.error('Failed to initialize chat');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkHumanSupportAvailability = useCallback(async () => {
    try {
      const response = await apiClient.get('/support-chat/human-support/availability');
      setHumanSupportAvailable(response.data.isAvailable);
      setBusinessHours(response.data.businessHours);
    } catch (error) {
      console.error('Failed to check human support availability:', error);
    }
  }, []);

  useEffect(() => {
    initializeChat();
    checkHumanSupportAvailability();
  }, [checkHumanSupportAvailability, initializeChat]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversation || isLoading) return;

    const messageText = newMessage.trim();
    const tempUserMessage = {
      id: Date.now(), // Temporary ID
      conversation_id: conversation.id,
      sender_type: 'user',
      sender_id: null,
      message_text: messageText,
      created_at: new Date().toISOString(),
      is_system_message: false
    };

    // Immediately add user message to UI
    setMessages(prev => [...prev, tempUserMessage]);
    setNewMessage('');
    setIsTyping(true);

    try {
      const response = await apiClient.post(`/support-chat/conversation/${conversation.id}/messages`, {
        message: messageText
      });

      // Reload messages to get the complete conversation with proper IDs and AI response
      const messagesResponse = await apiClient.get(`/support-chat/conversation/${conversation.id}/messages`);
      setMessages(messagesResponse.data.messages || []);

      // Update conversation state if human support was requested
      if (response.data.humanSupportRequested) {
        setConversation(prev => ({ ...prev, human_support_requested: true }));
      }

      if (!response.data.success) {
        toast.error(response.data.message || 'Failed to send message');
        // Remove the temporary message on error
        setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      // Remove the temporary message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
    } finally {
      setIsTyping(false);
    }
  };

  const requestHumanSupport = async () => {
    if (!conversation) return;

    try {
      const response = await apiClient.post(`/support-chat/conversation/${conversation.id}/request-human`);

      if (response.data.success) {
        toast.success(response.data.message);

        // Update conversation state and show banner
        setConversation(prev => ({ ...prev, human_support_requested: true }));
        setShowHumanSupportBanner(true);

        // Reload messages to show the system message
        const messagesResponse = await apiClient.get(`/support-chat/conversation/${conversation.id}/messages`);
        setMessages(messagesResponse.data.messages || []);
      } else {
        toast.error(response.data.message || 'Failed to request human support');
      }
    } catch (error) {
      console.error('Failed to request human support:', error);
      toast.error('Failed to request human support');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSenderIcon = (senderType) => {
    switch (senderType) {
      case 'user':
        return <UserIcon className="h-5 w-5" />;
      case 'ai':
        return <ComputerDesktopIcon className="h-5 w-5" />;
      case 'human_agent':
        return <UserIcon className="h-5 w-5 text-green-600" />;
      default:
        return <ComputerDesktopIcon className="h-5 w-5" />;
    }
  };

  const getSenderName = (senderType, senderName) => {
    switch (senderType) {
      case 'user':
        return 'You';
      case 'ai':
        return 'AI Assistant';
      case 'human_agent':
        return senderName || 'Support Agent';
      default:
        return 'System';
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Support Chat</h1>
              <p className="text-sm text-gray-600">Get help with your invoice management system</p>
            </div>
          </div>
          
          {/* Business Hours Info */}
          <div className="flex items-center space-x-2 text-sm">
            <ClockIcon className="h-5 w-5 text-gray-400" />
            <div className="text-right">
              <div className={`font-medium ${humanSupportAvailable ? 'text-green-600' : 'text-yellow-600'}`}>
                {humanSupportAvailable ? 'Human Support Available' : 'Outside Business Hours'}
              </div>
              {businessHours && (
                <div className="text-gray-500">
                  {businessHours.days}: {businessHours.start} - {businessHours.end} {businessHours.timezone}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-lg px-4 py-3 rounded-lg shadow-sm ${
                      message.sender_type === 'user'
                        ? 'bg-blue-600 text-white'
                        : message.is_system_message
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      {getSenderIcon(message.sender_type)}
                      <span className="text-sm font-medium">
                        {getSenderName(message.sender_type, message.sender_name)}
                      </span>
                      <span className="text-xs opacity-75">
                        {formatTime(message.created_at)}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.message_text}</p>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white text-gray-900 max-w-lg px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-2">
                      <ComputerDesktopIcon className="h-5 w-5" />
                      <span className="text-sm">AI Assistant is typing...</span>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Human Support Banner (if requested and banner is shown) */}
      {conversation?.human_support_requested && showHumanSupportBanner && (
        <div className="bg-blue-50 border-t border-blue-200 border-b border-blue-200 px-6 py-4">
          <div className="max-w-4xl mx-auto relative">
            <button
              onClick={() => setShowHumanSupportBanner(false)}
              className="absolute top-0 right-0 text-blue-400 hover:text-blue-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="pr-8">
              <div className="flex items-center space-x-2 mb-2">
                <UserIcon className="h-5 w-5 text-blue-600" />
                <h3 className="text-sm font-medium text-blue-900">
                  Human Support Requested
                </h3>
              </div>
              <p className="text-sm text-blue-700">
                {conversation.status === 'human_assigned'
                  ? 'A human agent has been assigned and will respond shortly.'
                  : 'Your request has been received. A human agent will assist you during business hours (9:00 AM - 5:00 PM PKT).'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex space-x-4 mb-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={conversation?.human_support_requested
                ? "Type your message for the human agent..."
                : "Type your message about the invoice system..."}
              className="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading || isTyping}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || isLoading || isTyping}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-6 py-3 rounded-lg transition-colors flex items-center space-x-2"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
              <span>Send</span>
            </button>
          </div>

          {/* Human Support Button (only show if not requested) */}
          {!conversation?.human_support_requested && (
            <button
              onClick={requestHumanSupport}
              disabled={!conversation}
              className={`w-full text-sm py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                humanSupportAvailable
                  ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border border-yellow-200'
              }`}
            >
              {humanSupportAvailable ? (
                <>
                  <UserIcon className="h-5 w-5" />
                  <span>Request Human Support (Available Now)</span>
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="h-5 w-5" />
                  <span>Request Human Support (Available Monday-Friday, 9:00 AM - 5:00 PM PKT)</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportChatPage;
